import { SEARCH_DATA_KEY } from 'api/search'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { Deal, OldTypes, OrganizationTableItem, Product, User } from 'types'
import { useSyncQueryCache } from '../organizations_table/useSyncQueryCache'

interface CreateDealPayload {
  dealData: Partial<Deal> // ✅ allow partial so not all fields must be defined
  products?: any[]
  currentUser: User
}

interface CreateDealResponse {
  deal: Deal
  products?: Product[]
  updatedProducts?: Product[]
  productCount?: number
}

export const usePostDeal = (_queryKey = ['deal']) => {
  const { showSnackbar } = useSnackbarContext()
  const queryClient = useQueryClient()
  const { syncDealsCacheAfterBatchUpdate: _syncDealsCacheAfterBatchUpdate } = useSyncQueryCache()
  return useMutation<CreateDealResponse, Error, CreateDealPayload>({
    mutationFn: async ({ dealData, products, currentUser }) => {
      const response = await axios.post(`${baseUrl}/aws/organizations_table/deal`, {
        dealData,
        products,
        currentUser,
      })
      return response.data
    },
    onMutate: async _newPayload => {
      const _oldDeals = queryClient.getQueryData<OrganizationTableItem[]>(SEARCH_DATA_KEY)
      //   await queryClient.cancelQueries({ queryKey: ['item', dealData?.PK, dealData?.SK] })
      //   const previousDeals = queryClient.getQueryData<Deal[]>(['item', dealData?.PK, dealData?.SK])

      //   queryClient.setQueryData<Deal[]>(['item', dealData?.PK, dealData?.SK], old =>
      //     old
      //       ? [...old, { ...newPayload.dealData, deal_uuid: 'temp-id' }]
      //       : [{ ...newPayload.dealData, deal_uuid: 'temp-id' }]
      //   )

      //   return { previousDeals }
    },
    onError: (error, _newPayload, _context) => {
      // if (context?.previousDeals) {
      //   queryClient.setQueryData(['item', dealData?.PK, dealData?.SK], context.previousDeals)
      // }
      showSnackbar(error?.message, 'error')
    },
    onSuccess: ({ deal, products: persistedProducts }) => {
      // 1) Update the combined cache — upsert deal AND inject products
      queryClient.setQueryData(SEARCH_DATA_KEY, (prev: OldTypes) => {
        // Upsert deal (append if missing, replace if it already exists)
        const exists = prev.deals.some(d => d.PK === deal.PK && d.SK === deal.SK)
        const deals = exists
          ? prev.deals.map(d => (d.PK === deal.PK && d.SK === deal.SK ? deal : d))
          : [...prev.deals, deal]

        // Inject products into cache so useDealData can find them immediately
        let nextProducts = prev.products ?? []
        if (Array.isArray(persistedProducts) && persistedProducts.length > 0) {
          const newSKs = new Set(persistedProducts.map(p => p.SK))
          nextProducts = [...nextProducts.filter(p => !newSKs.has(p.SK)), ...persistedProducts]
        }

        return { ...prev, deals, products: nextProducts }
      })

      // 2) (Nice) Preseed the per-deal key your pages/hooks might read
      queryClient.setQueryData(['deal', deal.deal_uuid], deal)

      showSnackbar('Deal created successfully', 'success')
    },
    onSettled: () => {
      // queryClient.invalidateQueries({ queryKey: ['item', dealData?.PK, dealData?.SK] })
    },
  })
}
